import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, PropsWithChildren } from 'react';
import type { To } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import crest from '@sorare/core/src/assets/user/crest.webp';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import type { TypographyVariant } from '@sorare/core/src/atoms/typography';
import { BrandXXS, LabelL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { ActiveUserAvatar } from '@sorare/core/src/components/user/ActiveUserAvatar';
import { ClubShield } from '@sorare/core/src/components/user/ClubShield';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { ANY_SPORT_MY_CLUB_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSendUserEngagementIncrement } from '@sorare/core/src/hooks/useSendUserEngagementIncrement';
import { isType } from '@sorare/core/src/lib/gql';
import { UserEngagementScoreIncrement_Source } from '@sorare/core/src/protos/webview/channel/native_messages';

import { Banner } from '#src/components/user/Banner/index.tsx';

import type { UserNetworkInfo_publicUserInfoInterface } from './__generated__/index.graphql.ts';

const Root = styled(Banner)`
  text-transform: capitalize;
  padding: var(--double-and-a-half-unit) var(--triple-unit) var(--triple-unit);
  height: 100%;
`;

const ClubName = styled(Horizontal)`
  min-width: min-content;
`;

type Props = {
  UserNameWrapper?: TypographyVariant;
  className?: string;
  user: UserNetworkInfo_publicUserInfoInterface;
  to?: To;
  disableRedirection?: boolean;
} & Partial<Pick<ComponentProps<typeof ActiveUserAvatar>, 'size'>>;

export const UserNetworkInfo = ({
  user,
  UserNameWrapper = LabelL,
  children,
  className,
  size = 5,
  to,
  disableRedirection,
}: PropsWithChildren<Props>) => {
  const { sport, generateSportContextPath } = useSportContext();
  const { sendUserEngagementIncrement } = useSendUserEngagementIncrement();

  const profile = {
    [Sport.FOOTBALL]: user.footballUserProfile,
    [Sport.NBA]: user.nbaUserProfile,
    [Sport.BASEBALL]: user.baseballUserProfile,
  }[sport];

  const shieldUrl = isType(profile, 'FootballUserSportProfile')
    ? profile.clubBadge?.pictureUrl
    : profile?.shieldUrl;

  const bannerUrl = isType(profile, 'FootballUserSportProfile')
    ? profile.background?.pictureUrl
    : profile?.bannerUrl;

  return (
    <LinkBox
      onClick={() =>
        sendUserEngagementIncrement(
          10,
          UserEngagementScoreIncrement_Source.CLICK_MY_CIRCLE_BUTTON
        )
      }
    >
      <Root bannerUrl={bannerUrl} className={className}>
        <Vertical gap={3} center className="text-center">
          <Vertical gap={1.5} center>
            <ActiveUserAvatar
              user={user}
              size={size}
              placeholderUrl={crest}
              rounded
            />
            <Vertical gap={0.5} center>
              <LinkOverlay
                as={disableRedirection ? 'div' : Link}
                to={
                  to ||
                  generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG, {
                    params: {
                      slug: user.slug,
                    },
                  })
                }
              >
                <UserNameWrapper bold className="text-center">
                  <UserName user={user} />
                </UserNameWrapper>
              </LinkOverlay>
              {!!shieldUrl && !!bannerUrl && (
                <ClubName gap={0.5}>
                  <ClubShield
                    size={2}
                    clubName={profile?.clubName}
                    shieldUrl={shieldUrl}
                  />
                  <BrandXXS color="var(--c-nd-600)">
                    <Ellipsis>{profile?.clubName}</Ellipsis>
                  </BrandXXS>
                </ClubName>
              )}
            </Vertical>
          </Vertical>
          <LinkOther as="div">{children}</LinkOther>
        </Vertical>
      </Root>
    </LinkBox>
  );
};

UserNetworkInfo.fragments = {
  user: gql`
    fragment UserNetworkInfo_publicUserInfoInterface on PublicUserInfoInterface {
      slug
      footballUserProfile {
        id
        clubName
        clubBadge {
          id
          pictureUrl
        }
        background {
          id
          pictureUrl
        }
      }
      nbaUserProfile {
        id
        clubName
        shieldUrl
        bannerUrl
      }
      baseballUserProfile {
        id
        clubName
        shieldUrl
        bannerUrl
      }
      ...ActiveUserAvatar_user
      ...UserName_publicUserInfoInterface
    }
    ${ActiveUserAvatar.fragments.user}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<UserNetworkInfo_publicUserInfoInterface>,
};
